import { type OpenApiSchemaMenuItem } from '#shared/api/schema';

export const MAX_MENU_NESTING_LEVELS = 3;

const MAX_MENU_TREE_LEVELS = MAX_MENU_NESTING_LEVELS + 1;

type MenuTreeItem = Pick<OpenApiSchemaMenuItem, 'id' | 'parent_id'>;

const normalizeParentId = (parentId: MenuTreeItem['parent_id']): string | undefined => parentId || undefined;

const getMenuItemLevels = (items: MenuTreeItem[]): Map<string, number> => {
    const itemsById = new Map(items.map((item) => [item.id, item]));
    const levels = new Map<string, number>();

    const getLevel = (item: MenuTreeItem, parentIds: Set<string> = new Set()): number => {
        const cachedLevel = levels.get(item.id);

        if (cachedLevel !== undefined) {
            return cachedLevel;
        }

        const parentId = normalizeParentId(item.parent_id);
        const parent = parentId ? itemsById.get(parentId) : undefined;

        if (!parent) {
            levels.set(item.id, 1);

            return 1;
        }

        if (parentIds.has(item.id)) {
            return Number.POSITIVE_INFINITY;
        }

        parentIds.add(item.id);

        const level = getLevel(parent, parentIds) + 1;

        levels.set(item.id, level);

        return level;
    };

    items.forEach((item) => getLevel(item));

    return levels;
};

const getSubtreeItemIds = (items: MenuTreeItem[], rootId: string): string[] => {
    const childrenByParentId = new Map<string, string[]>();

    items.forEach((item) => {
        const parentId = normalizeParentId(item.parent_id);

        if (!parentId) {
            return;
        }

        const childrenIds = childrenByParentId.get(parentId);

        if (childrenIds) {
            childrenIds.push(item.id);
        } else {
            childrenByParentId.set(parentId, [item.id]);
        }
    });

    const subtreeItemIds: string[] = [];
    const queue = [rootId];
    const visitedItemIds = new Set<string>();
    let queueIndex = 0;

    while (queueIndex < queue.length) {
        const itemId = queue[queueIndex++];

        if (!itemId || visitedItemIds.has(itemId)) {
            continue;
        }

        visitedItemIds.add(itemId);
        subtreeItemIds.push(itemId);
        queue.push(...(childrenByParentId.get(itemId) ?? []));
    }

    return subtreeItemIds;
};

const getMaxSubtreeLevel = (items: MenuTreeItem[], levels: Map<string, number>, rootId: string): number =>
    Math.max(...getSubtreeItemIds(items, rootId).map((itemId) => levels.get(itemId) ?? 1));

/**
 * TreeEditor использует уровень корневого пункта 0, поэтому он совпадает с глубиной вложенности.
 *
 * @kind helper
 * @param parentLevel - уровень родительского пункта в TreeEditor
 * @returns можно ли добавить дочерний пункт без превышения лимита
 */
export const canAddMenuItemChild = (parentLevel: number): boolean => parentLevel < MAX_MENU_NESTING_LEVELS;

/**
 * Разрешает старые глубокие деревья, но не позволяет создавать или увеличивать превышение лимита через DnD.
 *
 * @kind helper
 * @param currentItems - пункты меню до изменения дерева
 * @param nextItems - пункты меню после изменения дерева
 * @returns допустимо ли применить изменение дерева
 */
export const canApplyMenuItemsTreeChange = (currentItems: MenuTreeItem[], nextItems: MenuTreeItem[]): boolean => {
    if (currentItems.length !== nextItems.length) {
        return false;
    }

    const currentItemsById = new Map(currentItems.map((item) => [item.id, item]));
    const nextItemIds = new Set(nextItems.map((item) => item.id));

    if (currentItems.some((item) => !nextItemIds.has(item.id))) {
        return false;
    }

    const movedItems = nextItems.filter((item) => {
        const currentItem = currentItemsById.get(item.id);

        return normalizeParentId(currentItem?.parent_id) !== normalizeParentId(item.parent_id);
    });

    if (movedItems.length === 0) {
        return true;
    }

    const currentLevels = getMenuItemLevels(currentItems);
    const nextLevels = getMenuItemLevels(nextItems);

    return movedItems.every((item) => {
        const currentMaxLevel = getMaxSubtreeLevel(currentItems, currentLevels, item.id);
        const nextMaxLevel = getMaxSubtreeLevel(nextItems, nextLevels, item.id);

        return nextMaxLevel <= MAX_MENU_TREE_LEVELS || nextMaxLevel < currentMaxLevel;
    });
};
